import { createClient } from '@supabase/supabase-js';
import { Transaction, User } from '../types';

/**
 * Configuración de Supabase
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

/**
 * URL del Web App de Google Apps Script (Mantenida exclusivamente para el servicio de OTP)
 */
const SECURITY_OTP_API_URL = import.meta.env.VITE_GOOGLE_SCRIPT_URL || '';

// =============================================================================
// HELPERS DE FORMATEO Y PARSEO
// =============================================================================

function parseToNumber(val: any): number {
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (!val) return 0;
  const cleaned = String(val).trim().replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

function formatDateToFrontend(dbDateTime: string): { date: string, time: string } {
  if (!dbDateTime) return { date: '', time: '' };
  try {
    const dt = new Date(dbDateTime);
    if (isNaN(dt.getTime())) {
        const parts = dbDateTime.split(' ');
        const datePart = parts[0];
        const timePart = parts[1] || '00:00:00';
        const dParts = datePart.split('-');
        const formattedDate = dParts.length === 3 ? `${dParts[2]}/${dParts[1]}/${dParts[0]}` : datePart;
        return { date: formattedDate, time: timePart };
    }
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    const hours = String(dt.getHours()).padStart(2, '0');
    const minutes = String(dt.getMinutes()).padStart(2, '0');
    const seconds = String(dt.getSeconds()).padStart(2, '0');
    return { date: `${day}/${month}/${year}`, time: `${hours}:${minutes}:${seconds}` };
  } catch (e) {
    return { date: dbDateTime, time: '00:00:00' };
  }
}

function formatDateToDB(frontendDate: string, frontendTime: string): string {
  if (!frontendDate) return new Date().toISOString();
  const parts = frontendDate.split('/');
  if (parts.length !== 3) return frontendDate;
  return `${parts[2]}-${parts[1]}-${parts[0]} ${frontendTime || '00:00:00'}`;
}

function parseUniversalTimestamp(dateStr: string): number | null {
  try {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : Math.floor(date.getTime() / 1000);
  } catch (e) { return null; }
}

// =============================================================================
// FUNCIONES DE SUPABASE (DATABASE CLOUD)
// =============================================================================

export async function fetchTransactions(username?: string, portfolio?: string): Promise<Transaction[]> {
  let query = supabase.from('APP_Transacciones').select('*');
  
  if (username) {
    query = query.eq('Usuario_Cartera', username);
  }
  
  if (portfolio) {
    query = query.eq('portafolio', portfolio);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching transactions from Supabase:', error);
    return [];
  }

  return (data || []).map((t: any) => {
    const { date, time } = formatDateToFrontend(t.Fecha);
    const timestamp = parseUniversalTimestamp(t.Fecha) || Math.floor(Date.now() / 1000);
    return {
      id: t.IdTransaccion,
      ticket: t.Ticket,
      symbolName: t.Simbolo,
      price: parseToNumber(t.Precio),
      quantity: parseToNumber(t.Cantidad),
      total: parseToNumber(t.Total),
      date: date,
      time: time,
      ticketUsdt: t.TicketUSDT,
      status: t.Status,
      portfolioUser: t.Usuario_Cartera,
      timestamp,
      category: t.Categoria || 'criptomoneda',
      portfolio: t.portafolio // Mapeo directo sin default para respetar el valor de la DB
    };
  });
}

export async function fetchUserPortfolios(username: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('APP_Transacciones')
    .select('portafolio')
    .eq('Usuario_Cartera', username);

  if (error) {
    console.error('Error fetching portfolios:', error);
    return [];
  }

  // Extract unique, non-null, non-empty portfolios
  const uniquePortfolios = new Set<string>();
  (data || []).forEach((row: any) => {
    if (row.portafolio && row.portafolio.trim() !== '') {
      uniquePortfolios.add(row.portafolio);
    }
  });

  return Array.from(uniquePortfolios);
}

export async function fetchUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from('Login')
    .select('*');

  if (error) {
    console.error('[SUPABASE ERROR]', error.message);
    return [];
  }

  return (data || []).map((u: any) => ({
    id: String(u.ID || '').trim(),
    username: String(u.USUARIO || '').trim(),
    password: String(u.CONTRASEÑA || '').trim(),
    role: String(u.ROL || 'USUARIO').trim(),
    status: String(u.ESTADO || 'ACTIVO').trim(),
    date: u.FECHA || '',
    maxPortfolios: Number(u.MAX_PORTAFOLIOS || 1)
  }));
}

export async function saveTransactionToDB(transaction: Transaction): Promise<void> {
  const dbTimestamp = formatDateToDB(transaction.date, transaction.time);
  const dbPayload = {
    IdTransaccion: transaction.id,
    Ticket: transaction.ticket,
    Simbolo: transaction.symbolName,
    Precio: transaction.price,
    Cantidad: transaction.quantity,
    Total: transaction.total,
    Fecha: dbTimestamp,
    TicketUSDT: transaction.ticketUsdt,
    Status: transaction.status,
    Usuario_Cartera: transaction.portfolioUser,
    Categoria: transaction.category,
    portafolio: transaction.portfolio
  };
  const { error } = await supabase
    .from('APP_Transacciones')
    .upsert(dbPayload, { onConflict: 'IdTransaccion' });
  if (error) throw error;
}

export async function saveBatchTransactionsToDB(transactions: Transaction[]): Promise<void> {
  if (transactions.length === 0) return;

  const dbPayload = transactions.map(t => ({
    IdTransaccion: t.id,
    Ticket: t.ticket,
    Simbolo: t.symbolName,
    Precio: t.price,
    Cantidad: t.quantity,
    Total: t.total,
    Fecha: formatDateToDB(t.date, t.time),
    TicketUSDT: t.ticketUsdt,
    Status: t.status,
    Usuario_Cartera: t.portfolioUser,
    Categoria: t.category,
    portafolio: t.portfolio
  }));

  const { error } = await supabase
    .from('APP_Transacciones')
    .upsert(dbPayload, { onConflict: 'IdTransaccion' });
    
  if (error) throw error;
}

export async function deleteTransactionFromDB(id: string): Promise<void> {
  const { error } = await supabase
    .from('APP_Transacciones')
    .delete()
    .eq('IdTransaccion', id);
  if (error) throw error;
}

export async function saveUserToDB(user: User): Promise<void> {
  const dbPayload = {
    ID: user.id,
    USUARIO: user.username,
    CONTRASEÑA: user.password,
    ROL: user.role,
    ESTADO: user.status,
    FECHA: user.date || new Date().toISOString(),
    MAX_PORTAFOLIOS: user.maxPortfolios || 1
  };
  
  const { error } = await supabase
    .from('Login')
    .upsert(dbPayload, { onConflict: 'ID' });
    
  if (error) {
    console.error('[SUPABASE UPSERT ERROR]', error);
    throw error;
  }
}

/**
 * Actualiza la fecha de última conexión/actividad para el usuario especificado.
 */
export async function updateUserActivity(userId: string): Promise<void> {
  const now = new Date();
  // Ajuste opcional a hora de Ecuador si se desea persistir el string formateado
  const offset = -5;
  const ecuadorTime = new Date(now.getTime() + (offset * 60 * 60 * 1000));
  const dateStr = ecuadorTime.toISOString().replace('T', ' ').slice(0, 19);

  const { error } = await supabase
    .from('Login')
    .update({ FECHA: dateStr })
    .eq('ID', userId);

  if (error) {
    console.error('[SUPABASE UPDATE ACTIVITY ERROR]', error);
  }
}

export async function deleteUserFromDB(id: string): Promise<void> {
  const { error } = await supabase
    .from('Login')
    .delete()
    .eq('ID', id);
  if (error) throw error;
}

// =============================================================================
// SEGURIDAD OTP (GOOGLE APPS SCRIPT)
// =============================================================================

export async function sendOTPToEmail(email: string, otp: string): Promise<boolean> {
  try {
    await fetch(SECURITY_OTP_API_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ action: 'sendOTP', email, otp })
    });
    return true; 
  } catch (error) {
    console.error("OTP Send Error:", error);
    return false;
  }
}

export function parseUploadedCsv(text: string, targetPortfolio: string = 'Portafolio 1'): Transaction[] {
  const lines = text.trim().split('\n');
  const transactions: Transaction[] = [];

  const parseLine = (line: string) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result.map(s => s.replace(/^"|"$/g, '').trim());
  };

  // Start from 1 to skip header (Assuming first row is header)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const cols = parseLine(line);
    
    // Expecting at least 6 columns based on provided format:
    // Date, Token, Type, Price, Amount, Total Value
    if (cols.length < 6) continue;

    const dateStrRaw = cols[0];
    const token = cols[1];
    const type = cols[2];
    const priceStr = cols[3];
    const amountStr = cols[4];
    const totalStr = cols[5];

    const price = parseFloat(priceStr.replace(/,/g, '')) || 0;
    const quantity = parseFloat(amountStr.replace(/,/g, '')) || 0;
    const total = parseFloat(totalStr.replace(/,/g, '')) || 0;

    let status = 'Compra';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('sell') || lowerType.includes('venta')) status = 'Venta';
    else if (lowerType.includes('buy') || lowerType.includes('compra')) status = 'Compra';

    let dateDisplay = '';
    let timeDisplay = '00:00:00';
    let timestamp = 0;

    try {
        // Handle "2024-12-02 22:40:00"
        const d = new Date(dateStrRaw);
        if (!isNaN(d.getTime())) {
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            dateDisplay = `${day}/${month}/${year}`;
            timeDisplay = d.toTimeString().split(' ')[0]; // HH:MM:SS
            timestamp = Math.floor(d.getTime() / 1000);
        } else {
            // Fallback for current date if parse fails
            dateDisplay = new Date().toLocaleDateString('es-ES');
            timestamp = Math.floor(Date.now() / 1000);
        }
    } catch (e) {
        dateDisplay = '01/01/2024';
        timestamp = Math.floor(Date.now() / 1000);
    }

    const ticket = token.toUpperCase().includes('USD') ? token.toUpperCase() : `${token.toUpperCase()}-USD`;

    transactions.push({
      id: `csv-${i}-${Date.now()}`,
      ticket: ticket,
      symbolName: token.toUpperCase(),
      price: price,
      quantity: quantity,
      total: total,
      date: dateDisplay,
      time: timeDisplay,
      ticketUsdt: ticket.replace(/[-/]/g, '').replace('USD', 'USDT'),
      status: status,
      timestamp: timestamp,
      portfolioUser: 'csv',
      category: 'criptomoneda', // Default
      portfolio: targetPortfolio
    });
  }

  return transactions;
}